/**
 * Utility functions for secure file downloads using HttpOnly auth cookies.
 */

/**
 * Fetch and download file via Fetch API.
 * Supports both direct URLs and form submissions.
 *
 * @param urlOrForm - Download URL or HTML form element
 * @param options - Configuration (fileName, openInNewTab)
 */
export async function fetchAndDownloadFile(
  urlOrForm: string | HTMLFormElement,
  options: { fileName?: string; openInNewTab?: boolean; download?: boolean } = {},
): Promise<void> {
  const { openInNewTab = false, download = true } = options
  try {
    // Determine URL and body based on input type
    let url: string
    let body: FormData | null = null

    if (typeof urlOrForm === 'string') {
      // Direct URL download
      url = urlOrForm
    } else {
      // Form submission
      url = urlOrForm.action
      body = new FormData(urlOrForm)
    }

    const response = await fetch(url, {
      method: 'POST',
      body: body,
      credentials: 'include',
    })
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const blob = await response.blob()

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition')
    const fileName = extractFileNameFromContentDisposition(contentDisposition, options.fileName || 'download')

    // Create object URL and trigger download
    const objectUrl = globalThis.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.target = openInNewTab ? '_blank' : '_self'
    if (download) {
      link.setAttribute('download', fileName)
    }

    document.body.appendChild(link)
    link.click()
    link.remove()

    setTimeout(() => globalThis.URL.revokeObjectURL(objectUrl), 100)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

/**
 * Extract filename from Content-Disposition header
 * Supports both 'filename' and 'filename*' (RFC 5987) formats
 */
export function extractFileNameFromContentDisposition(
  contentDisposition: string | null,
  defaultFileName: string = 'download',
): string {
  if (!contentDisposition) {
    return defaultFileName
  }

  const encodedFileNameMatch = /filename\*=([^']*)'[^']*'([^;,]+)/i.exec(contentDisposition)
  if (encodedFileNameMatch?.[2]) {
    try {
      return decodeURIComponent(encodedFileNameMatch[2])
    } catch (error) {
      console.warn('Failed to decode filename from Content-Disposition:', error)
      return encodedFileNameMatch[2]
    }
  }

  const fileNameMatch = /filename=(?:"([^"]+)"|([^;,]+))/i.exec(contentDisposition)

  if (fileNameMatch && (fileNameMatch[1] || fileNameMatch[2])) {
    let fileName = fileNameMatch[1] || fileNameMatch[2] || defaultFileName
    // Decode URI encoded filenames (for special chars, accents..)
    try {
      fileName = decodeURIComponent(fileName)
    } catch (error) {
      // If decoding fails, log error and use the original value
      console.warn('Failed to decode filename from Content-Disposition:', error)
    }
    return fileName
  }
  return defaultFileName
}
